import React, { useState, useEffect } from "react";
import { FaSignOutAlt } from "react-icons/fa";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from '../../contexts/LanguageContext';
import { useAuth } from "../../App";
import LogoText from "../../images/logo_txt.svg";

const DashboardHeader = ({ isAuthenticated, user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { logout: authLogout } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Handle scroll prevention when modal is open
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showModal]);

  const handleLogOut = async () => {
    setIsLoggingOut(true);
    
    try {
      // Use the auth context logout function which handles both platforms
      if (authLogout) {
        await authLogout();
      }
      
      // Also call the passed onLogout prop if provided
      if (onLogout) {
        await onLogout();
      }
      
      // Clear all localStorage items that might contain user data
      const itemsToRemove = [
        'easysubs-language',
        'userID',
        'userName',
        'country',
        'userType',
        'token',
        'authToken',
        'user',
        'jwt_token'
      ];
      
      itemsToRemove.forEach(item => localStorage.removeItem(item));
      
      // Clear sessionStorage as well
      sessionStorage.clear();
      
      // Clear cookies (for web platform)
      if (typeof document !== 'undefined') {
        document.cookie.split(";").forEach(function(c) {
          document.cookie = c
            .replace(/^ +/, "")
            .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });
        
        // Additional cleanup for specific cookies
        document.cookie = "authToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        document.cookie = "jwt=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      }
      
    } catch (error) {
      console.error('Logout error:', error);
      // Even if there's an error, we should still redirect to login
    } finally {
      setShowModal(false);
      setIsLoggingOut(false);
      
      // Always redirect to /demo - AuthPage handles both mobile and desktop
      // Use replace: true to prevent going back to protected routes
      navigate("/demo", { replace: true });
      
      // Force a hard reload after a short delay to ensure clean state
      setTimeout(() => {
        if (window.location.pathname !== "/demo") {
          window.location.href = "/demo";
        }
      }, 100);
    }
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/dashboard' || path === '/mobile-dashboard') return t('bottomNav.home', 'Dashboard');
    if (path === '/notifications' || path === '/mobile-notifications') return t('bottomNav.notifications', 'Notifications');
    if (path === '/marketplace' || path === '/mobile-marketplace') return t('bottomNav.marketplace', 'Marketplace');
    if (path === '/share-subs' || path === '/mobile-share-subs') return t('bottomNav.shareSubs', 'Share Subs');
    if (path === '/connectBank' || path === '/mobile-connectBank') return t('bottomNav.connectBank', 'Connect Bank');
    if (path === '/partners' || path === '/mobile-partners') return t('bottomNav.partners', 'Partners');
    if (path === '/account-settings' || path === '/mobile-account-settings') return t('bottomNav.settings', 'Settings');
    if (path === '/upgrade' || path === '/mobile-upgrade') return t('accountSettings.subscription.upgrade', 'Upgrade');
    if (path === '/premium') return t('features.premiumTag', 'Premium');
    return 'EasySubs';
  };

  return (
    <header className="flex items-center p-4 relative">
      {/* Left: Logo */}
      <Link
        to="/dashboard"
        className="text-2xl font-bold text-customBlue flex items-center flex-shrink-0"
        style={{ color: "#263D95" }}
      >
        <img src={LogoText} alt="EasySubs" style={{ width: "75px", height: "auto" }} />
      </Link>

      {/* Center: Dashboard Title - Absolutely centered */}
      <h1 className="text-2xl font-bold text-customBlue absolute left-1/2 transform -translate-x-1/2">
        {getPageTitle()}
      </h1>

      {/* Right: Sign-out Button */}
      {isAuthenticated ? (
        <button
          onClick={() => setShowModal(true)}
          style={{ border: "none", background: "none", cursor: "pointer" }}
          className="flex items-center justify-center ml-auto flex-shrink-0"
          aria-label="Logout"
          disabled={isLoggingOut}
        >
          <FaSignOutAlt
            style={{ color: "red", fontSize: "24px", opacity: isLoggingOut ? 0.5 : 1 }}
            className="text-customBlue text-2xl"
          />
        </button>
      ) : (
        <div className="w-6 flex items-center ml-auto flex-shrink-0"></div>
      )}

      {/* Custom Confirmation Modal */}
      {showModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <p>{t('common.confirmLogout', 'Are you sure you want to log out?')}</p>
            <div style={styles.modalButtons}>
              <button 
                onClick={handleLogOut} 
                style={styles.confirmButton}
                disabled={isLoggingOut}
              >
                {isLoggingOut ? t('common.processing', 'Processing...') : t('common.yesLogout', 'Yes, Log Out')}
              </button>
              <button 
                onClick={() => setShowModal(false)} 
                style={styles.cancelButton}
                disabled={isLoggingOut}
              >
                {t('common.cancel', 'Cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

const styles = {
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
    backdropFilter: "blur(2px)",
  },
  modal: {
    backgroundColor: "#fff",
    padding: "20px",
    borderRadius: "8px",
    textAlign: "center",
    zIndex: 1001,
    pointerEvents: "auto",
    minWidth: "300px",
    maxWidth: "90%",
  },
  modalButtons: {
    display: "flex",
    justifyContent: "center",
    gap: "10px",
    marginTop: "20px",
  },
  confirmButton: {
    backgroundColor: "#dc2626",
    color: "#fff",
    border: "none",
    padding: "10px 20px",
    borderRadius: "5px",
    cursor: "pointer",
    fontWeight: "bold",
    minWidth: "120px",
  },
  cancelButton: {
    backgroundColor: "#6b7280",
    color: "#fff",
    border: "none",
    padding: "10px 20px",
    borderRadius: "5px",
    cursor: "pointer",
    fontWeight: "bold",
    minWidth: "120px",
  },
};

export default DashboardHeader;